import { useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { ApiError } from '@/types/api.types';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';

import { AddressTypeahead } from '../components/AddressTypeahead';
import { useBusinessProfile } from '../hooks/useBusinessProfile';
import { useBusinessSettingsStore } from '../store/businessSettingsStore';
import type { BusinessProfileData, UpdateProfileRequest } from '../types';

interface Props {
  onNext: () => void;
  onBack: () => void;
}

function ArrowRight() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4" aria-hidden="true">
      <path
        d="M3 8h10M9 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowLeft() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4" aria-hidden="true">
      <path
        d="M13 8H3M7 12l-4-4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function Step2BusinessProfile({ onNext, onBack }: Props) {
  const { t } = useTranslation();
  const store = useBusinessSettingsStore();
  const { submitProfile, isPending } = useBusinessProfile();
  const [address, setAddress] = useState(store.profile?.address ?? '');
  const [serverError, setServerError] = useState<string | null>(null);

  const schema = useMemo(
    () =>
      z.object({
        businessName: z.string().min(1, t('onboarding.step2.businessNameRequired')),
        vanityUrl: z
          .string()
          .min(1, t('onboarding.step2.vanityUrlRequired'))
          .regex(/^[a-z0-9-]+$/, t('onboarding.step2.vanityUrlInvalid')),
        phone: z.string().optional(),
        taxRegNumber: z.string().optional(),
        description: z.string().optional(),
      }),
    [t],
  );

  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      businessName: store.profile?.businessName ?? '',
      vanityUrl: store.profile?.vanityUrl ?? '',
      phone: store.profile?.phone ?? '',
      taxRegNumber: store.profile?.taxRegNumber ?? '',
      description: store.profile?.description ?? '',
    },
  });

  function onSubmit(values: FormValues) {
    setServerError(null);

    const localProfile: BusinessProfileData = {
      businessName: values.businessName,
      vanityUrl: values.vanityUrl,
      phone: values.phone ?? '',
      taxRegNumber: values.taxRegNumber ?? '',
      address,
      description: values.description ?? '',
    };

    const taxRegNumber = values.taxRegNumber?.trim();
    const request: UpdateProfileRequest = {
      businessName: values.businessName,
      bookingSubdomain: values.vanityUrl,
      businessDescription: values.description || undefined,
      ...(taxRegNumber && {
        tax: {
          taxRegistrationNumber: taxRegNumber,
          taxRegistrationType: 'VAT',
        },
      }),
    };

    submitProfile(request, {
      onSuccess: () => {
        store.setProfile(localProfile);
        onNext();
      },
      onError: (error) => {
        const apiError = error as ApiError;
        if (apiError.code === 'SUBDOMAIN_UNAVAILABLE') {
          setError('vanityUrl', { message: t('onboarding.step2.subdomainUnavailable') });
        } else if (apiError.code === 'VALIDATION_ERROR') {
          const fields = apiError.fields;
          let hadFieldError = false;
          if (fields?.bookingSubdomain) {
            setError('vanityUrl', { message: fields.bookingSubdomain });
            hadFieldError = true;
          }
          if (fields?.businessName) {
            setError('businessName', { message: fields.businessName });
            hadFieldError = true;
          }
          if (!hadFieldError) {
            setServerError(t('onboarding.step2.profileSaveFailed'));
          }
        } else if (apiError.code === 'FORBIDDEN') {
          setServerError(t('onboarding.step2.forbidden'));
        } else if (apiError.code === 'ACCOUNT_NOT_FOUND') {
          setServerError(t('onboarding.step2.accountNotFound'));
        } else {
          setServerError(t('onboarding.step2.profileSaveFailed'));
        }
      },
    });
  }

  return (
    <div>
      <h2 className="font-display text-[2rem] font-semibold leading-tight text-foreground">
        {t('onboarding.step2.title')}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">{t('onboarding.step2.subtitle')}</p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="mt-8 space-y-4">
        {serverError && (
          <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {serverError}
          </div>
        )}
        <Input
          label={t('onboarding.step2.businessName')}
          placeholder={t('onboarding.step2.businessNamePlaceholder')}
          error={errors.businessName?.message}
          {...register('businessName')}
        />

        {/* Vanity URL */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-foreground">
            {t('onboarding.step2.vanityUrl')}
          </label>
          <div
            className="flex items-center rounded-sm border border-input bg-surface focus-within:ring-2 focus-within:ring-brand focus-within:ring-offset-1"
            style={errors.vanityUrl ? { borderColor: 'hsl(var(--color-destructive))' } : undefined}
          >
            <span className="select-none whitespace-nowrap pl-3 text-sm text-muted-foreground">
              https://
            </span>
            <input
              type="text"
              placeholder={t('onboarding.step2.vanityUrlPlaceholder')}
              aria-label={t('onboarding.step2.vanityUrl')}
              className="h-10 flex-1 bg-transparent px-1 text-sm focus:outline-none"
              {...register('vanityUrl')}
            />
            <span className="select-none whitespace-nowrap pr-3 text-sm text-muted-foreground">
              .calendo.app
            </span>
          </div>
          {errors.vanityUrl && (
            <p role="alert" className="text-sm text-destructive">
              {errors.vanityUrl.message}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label={t('onboarding.step2.phone')}
            placeholder={t('onboarding.step2.phonePlaceholder')}
            type="tel"
            {...register('phone')}
          />
          <Input
            label={t('onboarding.step2.taxRegNumber')}
            placeholder={t('onboarding.step2.taxRegNumberPlaceholder')}
            {...register('taxRegNumber')}
          />
        </div>

        <AddressTypeahead
          label={t('onboarding.step2.address')}
          placeholder={t('onboarding.step2.addressPlaceholder')}
          value={address}
          onChange={setAddress}
        />

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-foreground">
            {t('onboarding.step2.description')}
          </label>
          <textarea
            rows={3}
            placeholder={t('onboarding.step2.descriptionPlaceholder')}
            className="w-full resize-none rounded-sm border border-input bg-surface px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1"
            {...register('description')}
          />
        </div>

        <div className="flex items-center justify-between pt-2">
          <Button type="button" variant="ghost" size="lg" leftIcon={<ArrowLeft />} onClick={onBack}>
            {t('onboarding.back')}
          </Button>
          <Button type="submit" size="lg" rightIcon={<ArrowRight />} isLoading={isPending}>
            {t('onboarding.continue')}
          </Button>
        </div>
      </form>
    </div>
  );
}
